import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Copy, FolderGit2, LayoutGrid, Pencil, Plus, Search, SquareCheckBig, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Avatar, EmptyState, Skeleton } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { Hint } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/markdown";
import { agentHandles } from "@/components/mention-text";
import { DeleteProjectDialog, ProjectForm } from "@/components/project-forms";
import { ProjectEnvSection } from "@/components/project-env";
import { shortPath } from "@/components/project-select";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { errorText } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import { useCan } from "@/lib/auth";
import { previewText } from "@/lib/preview-text";
import { takeProjectFocus, useProjectFocusRequest } from "@/lib/project-focus";
import type { Project, ProjectOverview, ProjectUsage, User } from "@/lib/types";
import { cn } from "@/lib/utils";

const indexOverviews = (data: { overviews: ProjectOverview[] }) => new Map(data.overviews.map((entry) => [entry.projectId, entry]));
const NO_OVERVIEWS = new Map<string, ProjectOverview>();

/** Past this many projects the page offers a filter box. */
const SEARCH_THRESHOLD = 6;

/**
 * The registered directories, as a page of cards with one fixed rhythm: name, path,
 * the description cut to three plain lines, and a footer of what uses it. Master
 * writes long, newcomer-friendly descriptions on purpose, so a card that grew with
 * its text made the list unreadable; the full text, markdown and all, is one tap
 * away in the detail sheet, which is also where editing and deleting live.
 *
 * Registering and editing are admin work (the server refuses anyone else), so a
 * member or viewer gets the same page read-only.
 */
export function ProjectsView({
  projects,
  users,
  showTitle = true,
  addRequest,
  onChanged,
  onOpenBoard,
  onOpenTask,
  onOpenAgent,
}: {
  projects: Project[];
  users: User[];
  /** The page's own title row. Off with tabs, whose top bar names the view and carries the +. */
  showTitle?: boolean;
  /** Bumped by the tab layout's top bar to open the register dialog from outside. */
  addRequest?: number;
  onChanged: () => void;
  onOpenBoard: (boardId: string) => void;
  onOpenTask: (taskId: string) => void;
  onOpenAgent: (agentId: string) => void;
}) {
  const canManage = useCan("admin");
  const overviewsQuery = useQuery({ queryKey: qk.projectOverviews(), queryFn: api.projectOverviews, select: indexOverviews });
  const overviews = overviewsQuery.data ?? (overviewsQuery.isError ? NO_OVERVIEWS : null);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (addRequest && canManage) setAdding(true);
  }, [addRequest, canManage]);

  // An agent's "Open project" lands here with that project's sheet to open.
  const focusRequest = useProjectFocusRequest();
  useEffect(() => {
    if (!focusRequest) return;
    takeProjectFocus();
    setQuery("");
    setOpenId(focusRequest);
  }, [focusRequest]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return projects;
    return projects.filter((project) =>
      [project.name, project.slug, project.hostPath, project.description ?? ""].some((field) =>
        field.toLowerCase().includes(needle),
      ),
    );
  }, [projects, query]);

  const missing = overviews ? projects.filter((project) => overviews.get(project.id)?.exists === false).length : 0;
  const open = projects.find((project) => project.id === openId) ?? null;

  return (
    <Page>
      <PageHeader
        title="Projects"
        showTitle={showTitle}
        description={
          <>
            {projects.length} director{projects.length === 1 ? "y" : "ies"} that boards, cards and agents work in
            {missing > 0 ? <span className="text-destructive"> · {missing} missing</span> : null}
          </>
        }
        actions={
          showTitle && canManage ? (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus /> Add project
            </Button>
          ) : null
        }
      />

      {projects.length > SEARCH_THRESHOLD ? (
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter by name, path or description"
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      ) : null}

      {projects.length === 0 ? (
        <EmptyState
          icon={<FolderGit2 />}
          title="No projects yet"
          hint="A project is a directory on this machine. Point a board or a card at one, and its agent works inside it — with permission to change it."
          className="py-12"
          action={
            canManage ? (
              <Button size="sm" className="mt-1" onClick={() => setAdding(true)}>
                <Plus /> Add project
              </Button>
            ) : undefined
          }
        />
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-muted-foreground">No project matches “{query.trim()}”.</p>
      ) : null}

      <div className="grid gap-2 win-md:grid-cols-2 win-xl:grid-cols-3">
        {filtered.map((project) => (
          <ProjectTile
            key={project.id}
            project={project}
            overview={overviews?.get(project.id) ?? null}
            loading={overviews === null}
            users={users}
            onOpen={() => setOpenId(project.id)}
          />
        ))}
      </div>

      <ProjectSheet
        project={open}
        overview={open ? overviews?.get(open.id) ?? null : null}
        users={users}
        canManage={canManage}
        onClose={() => setOpenId(null)}
        onChanged={onChanged}
        onOpenBoard={(boardId) => {
          setOpenId(null);
          onOpenBoard(boardId);
        }}
        onOpenTask={onOpenTask}
        onOpenAgent={(agentId) => {
          setOpenId(null);
          onOpenAgent(agentId);
        }}
      />

      <Dialog open={adding} onOpenChange={setAdding}>
        {/* `[&>*]:min-w-0`: DialogContent is a grid, and a pasted path is exactly the
            unbreakable string that would otherwise widen it past the screen. */}
        <DialogContent className="max-w-2xl [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>Add a project</DialogTitle>
            <DialogDescription>
              A directory on the machine running this board. Point a board or a card at it, and an{" "}
              <span className="font-mono text-[11px]">@agent</span> request there is carried out{" "}
              <span className="font-medium text-foreground">inside that directory</span> — with the codebase's own
              context, and with permission to change it.
            </DialogDescription>
          </DialogHeader>
          <ProjectForm
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              onChanged();
              toast.success("Project registered");
            }}
          />
        </DialogContent>
      </Dialog>
    </Page>
  );
}

/**
 * One project, the same height as every other whatever its description: each row
 * is one line (the name and path truncate) and the description box is always
 * three lines tall, blank or not.
 */
function ProjectTile({
  project,
  overview,
  loading,
  users,
  onOpen,
}: {
  project: Project;
  overview: ProjectOverview | null;
  loading: boolean;
  users: User[];
  onOpen: () => void;
}) {
  const description = project.description ? previewText(project.description) : "";
  return (
    // A div rather than a button, because the copy button sits inside it.
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${project.name}`}
      className={cn(
        "flex min-w-0 cursor-pointer flex-col rounded-lg border bg-card px-3.5 py-3 text-left transition-colors hover:border-ring/40 active:bg-muted/50",
        overview?.exists === false ? "border-destructive/40" : "border-border/70",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <FolderGit2 className="size-4 shrink-0 text-primary" />
        <p className="min-w-0 flex-1 truncate text-[14px] font-medium" title={project.name}>
          {project.name}
        </p>
        {overview?.exists === false ? <MissingBadge /> : null}
      </div>

      <div className="mt-1 flex min-w-0 items-center gap-1">
        <MiddleTruncatedPath path={shortPath(project.hostPath)} title={project.hostPath} className="flex-1" />
        <CopyPathButton path={project.hostPath} />
      </div>

      <p
        className={cn(
          // Three lines, always: the box holds its height when the text is short or absent.
          "mt-1.5 line-clamp-3 h-[calc(3*1.4em)] text-[12px] leading-[1.4em] break-words",
          description ? "text-muted-foreground" : "italic text-muted-foreground/60",
        )}
      >
        {description || "No description"}
      </p>

      <div className="mt-2.5 flex h-5 min-w-0 items-center gap-3 text-[11.5px] tabular-nums text-muted-foreground">
        {loading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <>
            <AgentChips agents={overview?.agents ?? []} users={users} />
            <span className="inline-flex shrink-0 items-center gap-1 [&_svg]:size-3">
              <LayoutGrid /> {overview?.boardCount ?? 0}
              <span className="sr-only"> boards</span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 [&_svg]:size-3">
              <SquareCheckBig /> {overview?.taskCount ?? 0}
              <span className="sr-only"> cards</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** The agent(s) working in a project: the first by avatar and handle, the rest as a count. */
function AgentChips({ agents, users }: { agents: ProjectOverview["agents"]; users: User[] }) {
  if (agents.length === 0) return <span className="min-w-0 flex-1 truncate opacity-70">No agent</span>;
  const [first] = agents;
  const user = users.find((entry) => entry.id === first!.id);
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5" title={agents.map((agent) => `@${agent.handle}`).join(", ")}>
      <Avatar name={first!.name} icon={user?.icon} iconColor={user?.iconColor} size="sm" />
      <span className="truncate text-foreground/85">@{first!.handle}</span>
      {agents.length > 1 ? <span className="shrink-0">+{agents.length - 1}</span> : null}
    </span>
  );
}

const MissingBadge = () => (
  <Hint label="The directory is not there any more. Edit the project to point it at where it moved.">
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/15 px-1.5 text-[10.5px] font-semibold text-destructive [&_svg]:size-3">
      <AlertTriangle /> Missing
    </span>
  </Hint>
);

/**
 * A path cut in the middle, so both the root and the folder that names the project
 * stay visible: the tail (the last segment, within reason) never shrinks, and the
 * head truncates with an ellipsis. The full path is the title, and the copy button.
 */
export function MiddleTruncatedPath({ path, title, className }: { path: string; title?: string; className?: string }) {
  const lastSlash = path.lastIndexOf("/", path.length - 2);
  // The last segment whole (with its slash), unless it is itself too long to keep.
  const tailLength = Math.min(path.length - Math.max(lastSlash, 0), 28);
  const head = path.slice(0, path.length - tailLength);
  const tail = path.slice(path.length - tailLength);
  return (
    <span className={cn("flex min-w-0 font-mono text-[11px] text-muted-foreground", className)} title={title ?? path}>
      <span className="truncate">{head}</span>
      <span className="shrink-0 whitespace-pre">{tail}</span>
    </span>
  );
}

function CopyPathButton({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Hint label="Copy the path">
      <Button
        variant="ghost"
        size="icon-sm"
        className="-my-1 size-6 shrink-0 text-muted-foreground"
        aria-label="Copy the path"
        onClick={(event) => {
          event.stopPropagation();
          void navigator.clipboard
            ?.writeText(path)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1200);
            })
            .catch(() => toast.error("Could not copy the path"));
        }}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </Hint>
  );
}

/**
 * Everything about one project: the full path, the description as markdown, what
 * uses it, and — for an admin — editing it and deleting it. Usage is fetched when
 * the sheet opens: it is the same read the delete dialog makes, and names the
 * boards and cards rather than counting them.
 */
function ProjectSheet({
  project,
  overview,
  users,
  canManage,
  onClose,
  onChanged,
  onOpenBoard,
  onOpenTask,
  onOpenAgent,
}: {
  project: Project | null;
  overview: ProjectOverview | null;
  users: User[];
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
  onOpenBoard: (boardId: string) => void;
  onOpenTask: (taskId: string) => void;
  onOpenAgent: (agentId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState<Project | null>(null);
  const handles = useMemo(() => agentHandles(users), [users]);
  const usageQuery = useQuery({
    queryKey: qk.projectUsage(project?.id ?? ""),
    queryFn: () => api.projectUsage(project!.id),
    enabled: project !== null,
  });
  const usage: ProjectUsage | null = usageQuery.data ?? null;
  const usageError = usageQuery.isError && !usageQuery.data ? errorText(usageQuery.error, "Could not read what uses it") : null;

  useEffect(() => {
    setEditing(false);
  }, [project?.id]);

  return (
    <Sheet open={project !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="max-w-2xl" onOpenAutoFocus={(event) => event.preventDefault()}>
        {project ? (
          <>
            <div className="shrink-0 border-b border-border/70 px-5 pb-3.5 pt-4 pr-12">
              <SheetTitle className="flex min-w-0 items-start gap-2 text-base font-semibold tracking-tight">
                <FolderGit2 className="mt-1 size-4 shrink-0 text-primary" />
                <span className="min-w-0 break-words">{project.name}</span>
              </SheetTitle>
              <SheetDescription className="mt-0.5 font-mono text-[11px] text-muted-foreground">{project.slug}</SheetDescription>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 scrollbar-slim">
              {editing ? (
                <ProjectForm
                  project={project}
                  onCancel={() => setEditing(false)}
                  onSaved={() => {
                    setEditing(false);
                    onChanged();
                    toast.success("Project saved");
                  }}
                />
              ) : (
                <>
                  <section className="space-y-1.5">
                    <SectionLabel>Directory</SectionLabel>
                    <div className="flex items-start gap-1.5 rounded-md border border-border/70 bg-surface/40 px-2.5 py-2">
                      <code className="min-w-0 flex-1 break-all font-mono text-[12px]">{project.hostPath}</code>
                      <CopyPathButton path={project.hostPath} />
                    </div>
                    {overview?.exists === false ? (
                      <p className="flex items-start gap-1.5 text-[12px] text-destructive">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                        The directory is not there any more. If it moved, edit the project and point it at the new
                        place — its boards and cards keep working.
                      </p>
                    ) : null}
                    {project.mergeCheck ? (
                      <p className="break-all text-[12px] text-muted-foreground">
                        Check before merging a parallel run: <span className="font-mono text-foreground/85">{project.mergeCheck}</span>
                      </p>
                    ) : null}
                  </section>

                  <section className="space-y-1.5">
                    <SectionLabel>What it is</SectionLabel>
                    {project.description ? (
                      <Markdown text={project.description} handles={handles} className="text-[13px]" />
                    ) : (
                      <p className="text-[13px] italic text-muted-foreground">
                        No description. Every delegated run reads one, so a line or two on what this is and how to check
                        a change is worth adding.
                      </p>
                    )}
                  </section>

                  {canManage && overview?.exists !== false ? <ProjectEnvSection projectId={project.id} users={users} /> : null}

                  <section className="space-y-1.5">
                    <SectionLabel>Used by</SectionLabel>
                    {usageError ? (
                      <p className="text-[12px] text-destructive">{usageError}</p>
                    ) : usage === null ? (
                      <Skeleton className="h-16 w-full" />
                    ) : usage.boards.length + (usage.detachedBoards?.length ?? 0) + usage.tasks.length + usage.agents.length === 0 ? (
                      <p className="text-[13px] text-muted-foreground">Nothing points at this project yet.</p>
                    ) : (
                      <div className="space-y-3 text-[13px]">
                        {usage.agents.length > 0 ? (
                          <UsageList title="Agents">
                            {usage.agents.map((agent) => {
                              const user = users.find((entry) => entry.id === agent.id);
                              return (
                                <UsageRow key={agent.id} onClick={() => onOpenAgent(agent.id)}>
                                  <Avatar name={agent.name} icon={user?.icon} iconColor={user?.iconColor} size="sm" />
                                  <span className="min-w-0 flex-1 truncate">{agent.name}</span>
                                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">@{agent.handle}</span>
                                </UsageRow>
                              );
                            })}
                          </UsageList>
                        ) : null}
                        {usage.boards.length > 0 ? (
                          <UsageList title="Boards it is the only project of">
                            {usage.boards.map((board) => (
                              <UsageRow key={board.id} onClick={() => onOpenBoard(board.id)}>
                                <LayoutGrid className="size-3.5 shrink-0 text-muted-foreground" />
                                <span className="min-w-0 flex-1 truncate">{board.name}</span>
                                {board.archived ? <Badge variant="outline">archived</Badge> : null}
                                <span className="shrink-0 tabular-nums text-[11.5px] text-muted-foreground">
                                  {board.taskCount} card{board.taskCount === 1 ? "" : "s"}
                                </span>
                              </UsageRow>
                            ))}
                          </UsageList>
                        ) : null}
                        {(usage.detachedBoards?.length ?? 0) > 0 ? (
                          <UsageList title="Boards covering it among others">
                            {usage.detachedBoards.map((board) => (
                              <UsageRow key={board.id} onClick={() => onOpenBoard(board.id)}>
                                <LayoutGrid className="size-3.5 shrink-0 text-muted-foreground" />
                                <span className="min-w-0 flex-1 truncate">{board.name}</span>
                                {board.archived ? <Badge variant="outline">archived</Badge> : null}
                                {board.wasDefault ? (
                                  <span className="shrink-0 text-[11.5px] text-muted-foreground">default</span>
                                ) : null}
                              </UsageRow>
                            ))}
                          </UsageList>
                        ) : null}
                        {usage.tasks.length > 0 ? (
                          <UsageList title="Cards on other boards that name it">
                            {usage.tasks.map((task) => (
                              <UsageRow key={task.id} onClick={() => onOpenTask(task.id)}>
                                <SquareCheckBig className="size-3.5 shrink-0 text-muted-foreground" />
                                <span className="min-w-0 flex-1 truncate">{task.title}</span>
                                <span className="max-w-[40%] shrink-0 truncate text-[11.5px] text-muted-foreground">{task.boardName}</span>
                              </UsageRow>
                            ))}
                          </UsageList>
                        ) : null}
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>

            {canManage && !editing ? (
              <div className="flex shrink-0 items-center gap-2 border-t border-border/70 px-5 py-3 pb-[max(0.75rem,var(--safe-bottom,env(safe-area-inset-bottom)))]">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleting(project)}
                >
                  <Trash2 /> Delete
                </Button>
                <span className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Pencil /> Edit
                </Button>
              </div>
            ) : null}

            <DeleteProjectDialog
              project={deleting}
              onClose={() => setDeleting(null)}
              onDeleted={(gone, result) => {
                setDeleting(null);
                onClose();
                onChanged();
                toast.success(
                  result.deletedBoards + result.deletedTasks > 0
                    ? `Deleted "${gone.name}" with ${result.deletedBoards} board(s) and ${result.deletedTasks} card(s). Nothing on disk was touched.`
                    : `Deleted "${gone.name}". Nothing on disk was touched.`,
                );
              }}
            />
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
);

const UsageList = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-0.5">
    <p className="text-[11.5px] text-muted-foreground">{title}</p>
    <ul className="-mx-1.5">{children}</ul>
  </div>
);

const UsageRow = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <li>
    <button
      onClick={onClick}
      className="flex w-full min-w-0 items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-muted/60 active:bg-muted"
    >
      {children}
    </button>
  </li>
);
